import React, { useState } from 'react';
import { CustomFieldType } from './CustomFieldTypes';
import { useTranslation } from 'react-i18next';
import TextInput from '../basefields/TextInput';
import TextareaInput from '../basefields/TextareaInput';
import Checkbox from '../basefields/Checkbox';
import Dropdown from '../basefields/Dropdown';
import DateInput from '../basefields/DateInput';
import SwitchButton from 'components/buttons/SwitchButton';
import TabSelect from '../basefields/TabSelect';
import TimeInput from '../basefields/TimeInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faSignature } from '@fortawesome/free-solid-svg-icons';

const CustomFieldPreview: React.FC<{ field: CustomFieldType }> = ({ field }) => {
    const { t } = useTranslation();
    const [previewValue, setPreviewValue] = useState<any>(null);
    const [previewCheckboxValues, setPreviewCheckboxValues] = useState<string[]>([]);

    const label = field.label || t('custom_field.general.field_label_label');

    // Convert the label into a backend value
    const convertLabelToBackendValue = (label: string): string => {
        return label
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');
    };

    // Render the preview field
    const renderPreview = () => {
        switch (field.field_type) {
            case 'textarea':
                return (
                    <TextareaInput
                        name={convertLabelToBackendValue(field.label || '')}
                        rows={5}
                        value={previewValue || ''}
                        placeholder={field.placeholder || ''}
                        onChange={(value) => {
                            setPreviewValue(typeof value === 'string' ? value : value.target.value);
                        }}
                    />
                );
            case 'dropdown':
                return (
                    <Dropdown<any>
                        name={convertLabelToBackendValue(field.label || '')}
                        options={field.options || []}
                        selectedOption={null}
                        optionFormat='label'
                        optionMode='dot-text'
                        selectionFormat={(option) => option.label}
                        allowNoneOption={false}
                        showSearch={false}
                        disabled_selected='custom_field.general.select_option_label'
                        onChange={(value) => setPreviewValue(value)}
                    />
                );
            case 'checkbox':
                return (
                    <div className='preview-checkbox-group'>
                        {(field.options || [])
                            .filter(option => !option.deleted)
                            .map(option => {

                                const isChecked = previewCheckboxValues.includes(option.value);

                                return (
                                    <Checkbox
                                        key={option.id || option.uuid}
                                        name={option.value}
                                        label={option.label}
                                        align='left'
                                        value={isChecked}
                                        onChange={() => {
                                            if (isChecked) {
                                                setPreviewCheckboxValues(current =>
                                                    current.filter(value => value !== option.value)
                                                );
                                            } else {
                                                setPreviewCheckboxValues(current => [ ...current, option.value ]);
                                            }
                                        }}
                                    />
                                );
                            })}
                    </div>
                );
            case 'boolean':
                return (
                    <SwitchButton
                        value={previewValue || false}
                        onToggle={(isOn) => {
                            setPreviewValue(isOn);
                        }}
                    />
                );
            case 'yes_no':
            case 'acceptable':
            case 'compliant':
            case 'tabs':
                return (
                    <TabSelect<any>
                        options={(field.options || []).filter(option => !option.deleted)}
                        selectedOption={(field.options || []).find(option => option.value === previewValue) ?? null}
                        optionFormat='label'
                        backendField='value'
                        onChange={(value) => setPreviewValue(value)}
                    />
                );
            case 'amount':
                return (
                    <div className='preview-price-field'>
                        <span className='currency-prefix'>
                            €
                        </span>
                        <TextInput
                            name={convertLabelToBackendValue(field.label || '')}
                            placeholder='0,00'
                            value={previewValue || ''}
                            onChange={(value) => {
                                const cleanedValue = value.replace(/[^0-9.,-]/g, '');
                                setPreviewValue(cleanedValue);
                            }}
                        />
                    </div>
                );
            case 'number':
                return (
                    <TextInput
                        name={convertLabelToBackendValue(field.label || '')}
                        placeholder='0'
                        value={previewValue || ''}
                        onChange={(value) => {
                            const cleanedValue = value.replace(/[^0-9.,-]/g, '');
                            setPreviewValue(cleanedValue);
                        }}
                    />
                );
            case 'date':
                return (
                    <DateInput
                        name={convertLabelToBackendValue(field.label || '')}
                        value={previewValue || null}
                        onChange={(value) => setPreviewValue(value)}
                    />
                );
            case 'time':
                return (
                    <TimeInput
                        type="time"
                        name={convertLabelToBackendValue(field.label || '')}
                        value={previewValue || null}
                        onChange={(value) => {
                            setPreviewValue(value);
                        }}
                    />
                );
            case 'photo':
                return (
                    <div className='preview-upload'>
                        <FontAwesomeIcon icon={faCamera} />
                        <span>
                            Foto upload
                        </span>
                    </div>
                );
            case 'signature':
                return (
                    <div className='preview-upload'>
                        <FontAwesomeIcon icon={faSignature} />
                        <span>
                            Handtekening
                        </span>
                    </div>
                );
            default:
                return (
                    <TextInput
                        name={convertLabelToBackendValue(field.label || '')}
                        placeholder={field.placeholder || ''}
                        value={previewValue || ''}
                        onChange={(value) => setPreviewValue(value)}
                    />
                );
        }   
    };

    return (
        <div className='preview-card'>
            <label>
                {label}
                {field.required && (
                    <span className='required'>
                        *
                    </span>
                )}
            </label>

            <div>
                {renderPreview()}

                {field.description && (
                    <div className='helper-text'>
                        {field.description}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomFieldPreview;